import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Pill from '@/benefits-flow/Pill';
import { QUESTIONS } from '@/benefits-flow/screener/questions';
import QuestionField from '@/benefits-flow/screener/QuestionField';
import { ACTION_TONE } from '@/benefits-flow/tones';
import Screen from '@/karehero-app/explore/Screen';
import {
  PERSON,
  PROPOSAL,
} from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';
import {
  ConfirmPair,
  Note,
  ProposalRow,
} from '@/karehero-app/explore/benefits-calculator/Parts';

// Section C. The question arrives already answered, from something we already
// hold. No AI anywhere in this section — cross-form dedup works at the data
// layer today (questions share ids across forms), so the only thing missing is
// that nobody ever sees it. That makes this the cheapest mode on the page and
// the one that should probably ship first.
//
// It is also the shape every other mode terminates in, which is why it is
// argued here before the modes that generate proposals.

const noop = () => undefined;

export const C1Proposed = () => (
  <Screen
    title={PROPOSAL.invite}
    footer={<ConfirmPair confirm={PROPOSAL.confirm} change={PROPOSAL.change} />}
  >
    <Stack spacing={2}>
      {/* display='flex' so the chip hugs its label instead of stretching
          to the width of the column it sits in. */}
      <Box sx={{ display: 'flex' }}>
        <Pill label={PROPOSAL.badge} tone={ACTION_TONE.check} />
      </Box>
      <ProposalRow
        question={QUESTIONS[
          'care-recipient-needs-help-time-of-day'
        ].prompt.replaceAll('{name}', PERSON.name)}
        value='In the day'
        said={PROPOSAL.sources.held}
      />
    </Stack>
  </Screen>
);
C1Proposed.displayName = 'C1Proposed';

export const C2Confirmed = () => (
  <Screen title={PROPOSAL.invite}>
    <Stack spacing={2}>
      {/* display='flex' so the chip hugs its label instead of stretching
          to the width of the column it sits in. */}
      <Box sx={{ display: 'flex' }}>
        <Pill label={PROPOSAL.badge} tone={ACTION_TONE.check} />
      </Box>
      <ProposalRow
        question='Does Margaret need help through the day or night?'
        value='In the day'
        said={PROPOSAL.sources.held}
        state='confirmed'
      />
      <Note
        title='One question saved'
        icon='check'
        body='And the same answer is already in her Attendance Allowance form.'
      />
    </Stack>
  </Screen>
);
C2Confirmed.displayName = 'C2Confirmed';

// Changing an answer is the important half. Editing has to be easy enough that
// confirming stays honest — if changing is a chore, people confirm things that
// are wrong.
export const C3Changed = () => (
  <Screen title={PROPOSAL.changed.title} back>
    <Stack spacing={2}>
      <Typography variant='body2' color='text.secondary'>
        {PROPOSAL.changed.body}
      </Typography>
      <QuestionField
        question={QUESTIONS['care-recipient-needs-help-time-of-day']}
        recipientName={PERSON.name}
        value='care-recipient-needs-help-during-both'
        onChange={noop}
      />
    </Stack>
  </Screen>
);
C3Changed.displayName = 'C3Changed';

// The batch. Six questions, four of them already answered, so the calculator
// asks two. This is the tell-us-once promise at its smallest and most testable.
export const C4Batch = () => (
  <Screen
    title={PROPOSAL.batch.title}
    subtitle={PROPOSAL.batch.subtitle}
    footer={
      <Stack spacing={2}>
        <Button ariaLabel={PROPOSAL.batch.cta} isFullWidth>
          {PROPOSAL.batch.cta}
        </Button>
        <Button ariaLabel={PROPOSAL.batch.edit} variant='link' isFullWidth>
          {PROPOSAL.batch.edit}
        </Button>
      </Stack>
    }
  >
    <Stack spacing={1}>
      <ProposalRow
        question='Where does Margaret live?'
        value='England'
        said={PROPOSAL.sources.document}
      />
      <ProposalRow
        question='How old is Margaret?'
        value='66 or over'
        said={PROPOSAL.sources.derived}
      />
      <ProposalRow
        question='Does Margaret have a disability or long-term condition?'
        value='Yes'
        said={PROPOSAL.sources.held}
      />
      <ProposalRow
        question='Does Margaret have a partner or spouse?'
        value='No'
        said={PROPOSAL.sources.held}
      />
      <Note
        title='Still to ask'
        body='Her savings, and her weekly income. Neither is on anything we hold.'
      />
    </Stack>
  </Screen>
);
C4Batch.displayName = 'C4Batch';
